import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import type { SyncUserResponse } from '../xgrit-api/sync-user-response';
import { XGritApiService } from '../xgrit-api/xgrit-api.service';

@Injectable({
  providedIn: 'root',
})
export class MarketingSyncUserService {
  private syncUserSubject = new BehaviorSubject<SyncUserResponse | null>(null);

  readonly syncUser$: Observable<SyncUserResponse | null> = this.syncUserSubject.asObservable();

  constructor(private readonly xgritApiService: XGritApiService) {
    this.fetchSyncUser();
  }

  protected fetchSyncUser(): void {
    this.xgritApiService.getSyncUser().subscribe({
      next: (response) => {
        this.syncUserSubject.next(response);
      },
      error: () => {
        this.syncUserSubject.next({
          success: false,
          data: {
            isAuthenticated: false,
            initials: '',
            adaToken: '',
            ambassadorUrl: '',
            email: '',
            meta: { fieldsRequested: [], fieldsReturned: [], timestamp: '' },
          },
        });
      },
    });
  }

  /**
   * Returns the current sync user value synchronously.
   * May be null if the API call hasn't completed yet.
   */
  get syncUser(): SyncUserResponse | null {
    return this.syncUserSubject.getValue();
  }

  /**
   * Returns an observable that emits the first non-null SyncUserResponse.
   * Useful when you need to wait for the data to be available.
   */
  get syncUserReady$(): Observable<SyncUserResponse> {
    return this.syncUser$.pipe(
      filter((user): user is SyncUserResponse => user !== null),
      take(1),
    );
  }

  get isAuthenticated(): boolean {
    return this.syncUser?.success === true && this.syncUser?.data?.isAuthenticated === true;
  }

  get initials(): string {
    return this.syncUser?.data?.initials ?? '';
  }

  get adaToken(): string {
    return this.syncUser?.data?.adaToken ?? '';
  }

  get ambassadorUrl(): string {
    return this.syncUser?.data?.ambassadorUrl ?? '';
  }

  get email(): string {
    return this.syncUser?.data?.email ?? '';
  }
}
